import api from './api';

export interface Project {
  _id: string;
  name: string;
  description?: string;
  hoursTrackingEnabled?: boolean;
  archived?: boolean;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  userRole?: 'owner' | 'admin' | 'member' | 'viewer';
}

export interface TeamMember {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string;
  invitedBy: {
    _id: string;
    name: string;
    email: string;
  };
  projectId: string;
}

export interface TeamInvitation {
  _id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  projectId: {
    _id: string;
    name: string;
    description?: string;
  };
  invitedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  expiresAt: string;
}

export interface TeamData {
  members: TeamMember[];
  invitations: TeamInvitation[];
  userRole: 'owner' | 'admin' | 'member' | 'viewer';
}

// Buscar membros da equipe de um projeto
export const getTeamMembers = async (projectId: string): Promise<TeamData> => {
  try {
    const [membersResponse, invitationsResponse] = await Promise.all([
      api.get(`/projects/${projectId}/team/members`),
      api.get(`/projects/${projectId}/team/invitations`)
    ]);

    return {
      members: membersResponse.data.data.members,
      invitations: invitationsResponse.data.data.invitations,
      userRole: membersResponse.data.data.userRole
    };
  } catch (error) {
    console.error('Error fetching team data:', error);
    throw error;
  }
};

// Convidar novo membro
export const inviteTeamMember = async (
  projectId: string,
  email: string,
  role: 'admin' | 'member' | 'viewer'
): Promise<{ invitation: TeamInvitation; emailSent: boolean; emailError?: string }> => {
  try {
    const response = await api.post(`/projects/${projectId}/team/invite`, {
      email,
      role
    });
    return response.data.data;
  } catch (error) {
    console.error('Error inviting team member:', error);
    throw error;
  }
};

// Remover membro da equipe
export const removeTeamMember = async (
  projectId: string,
  userId: string
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/team/members/${userId}`);
  } catch (error) {
    console.error('Error removing team member:', error);
    throw error;
  }
};

// Alterar papel do membro
export const updateMemberRole = async (
  projectId: string,
  userId: string,
  role: 'admin' | 'member' | 'viewer'
): Promise<TeamMember> => {
  try {
    const response = await api.patch(`/projects/${projectId}/team/members/${userId}/role`, {
      role
    });
    return response.data.data.membership;
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

// Cancelar convite
export const cancelInvitation = async (
  projectId: string,
  invitationId: string
): Promise<void> => {
  try {
    await api.delete(`/projects/${projectId}/team/invitations/${invitationId}`);
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    throw error;
  }
};

// Buscar projetos baseado na participação do usuário (incluindo membros da equipe)
export const getProjects = async (archived?: boolean): Promise<Project[]> => {
  try {
    const response = await api.get('/projects/accessible', {
      params: archived !== undefined ? { archived } : {}
    });
    return response.data.data.projects;
  } catch (error) {
    console.error('Error fetching accessible projects:', error);
    throw error;
  }
};